"use client";

import { useState } from "react";
import {
  Notice,
  type Option,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  alphaOptions,
  ExampleButton,
  levelOptions,
} from "@/components/tools/stats-tests/kit";
import { SignTestResults } from "@/components/tools/stats-tests-more/sign-test-results";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput, parseNumberList } from "@/lib/parse-number";
import type { Alternative } from "@/lib/tool-kit/stats-tests/format";
import { signTest } from "@/lib/tool-kit/stats-tests-more/sign-test";

/** English strings. A translated content file overrides these through `ui`. */
export const SIGN_TEST_CALCULATOR_STRINGS = {
  inputLabel: "Input",
  pairedInput: "Two paired lists",
  singleInput: "One list and a median",
  firstLabel: "First measurement (e.g. before)",
  secondLabel: "Second measurement (e.g. after), in the same order",
  singleLabel: "Values — numbers separated by commas, spaces, or new lines",
  medianLabel: "Hypothesised median (H₀)",
  medianHint: "Use 0 to test a list of differences.",
  dataPlaceholder: "42, 38, 51, 45",
  valuesDetected: "one: value|other: values",
  alternativeLabel: "Alternative hypothesis",
  twoSided: "Two-sided: the median difference is not zero",
  less: "The median difference is negative",
  greater: "The median difference is positive",
  alternativeHint: "Differences are first − second (or value − median).",
  alphaLabel: "Significance level (α)",
  levelLabel: "Confidence level for the intervals",
  tryExample: "Try an example",
  exampleFirst: "42, 38, 51, 45, 36, 48, 40, 53, 39, 47, 44, 50",
  exampleSecond: "37, 35, 44, 46, 30, 39, 40, 45, 35, 37, 44, 41",
  exampleSingle: "12, 15, 9, 20, 17, 14, 11, 18, 13, 16",
  exampleMedian: "12",
  results: "Results",
  positive: "Positive differences (n⁺)",
  negative: "Negative differences (n⁻)",
  ties: "Zero differences (dropped)",
  nUsed: "Signed differences used (n)",
  pValue: "p-value (exact)",
  proportion: "Share positive (n⁺ / n)",
  decisionReject:
    "Reject H₀ at α = {alpha}: p = {p} is below α — the differences lean one way more than a fair coin would allow.",
  decisionKeep:
    "Do not reject H₀ at α = {alpha}: p = {p} is not below α — no evidence that the median difference is not zero.",
  pValuesHeading: "All three p-values",
  alternative: "Alternative",
  p: "p",
  signsHeading: "Signs of the differences",
  signsHint:
    "Each difference is classed as positive, negative, or zero; zeros are dropped before testing.",
  pair: "Pair",
  value: "Value",
  firstColumn: "First",
  secondColumn: "Second",
  difference: "Difference",
  sign: "Sign",
  plus: "+",
  minus: "−",
  zero: "0 (dropped)",
  signsHidden: "The sign table is shown for up to 40 values.",
  intervalsHeading: "Intervals",
  proportionInterval:
    "Share of positive differences: {share}, exact (Clopper–Pearson) {level} CI [{lo}, {hi}].",
  medianLine: "Median of the differences: {median}.",
  medianInterval:
    "Distribution-free {level} CI for the median: [{lo}, {hi}] — actual coverage {coverage} (the interval runs between the {k}th smallest and {k}th largest values).",
  medianIntervalNone:
    "With {total} values no distribution-free interval reaches {level} coverage; more data are needed.",
  normalLine:
    "Normal approximation with continuity correction: z = {z}, two-sided p = {p}. The exact p-value above is the one to report.",
  howCalculated: "How this was calculated",
  workingCounts:
    "n⁺ = {pos}, n⁻ = {neg}, zeros = {zero}; n = n⁺ + n⁻ = {n}. Under H₀ each signed difference is positive with probability ½, so n⁺ ~ Binomial({n}, ½).",
  workingTwo:
    "Two-sided p = 2 × P(X ≤ {low}) = 2 × {tail} = {p} (capped at 1).",
  workingLess: "P(X ≤ {k}) = {p}",
  workingGreater: "P(X ≥ {k}) = {p}",
  apaHeading: "APA-style result",
  apaTemplate: "*S* = {s}, *n* = {n}, exact *p* {p}",
  apaNote:
    "S is the number of positive differences and n excludes zero differences. Report the median difference (or each group's median) with the test, and say the sign test was used.",
  copyApa: "Copy APA result",
  copied: "Copied!",
  promptPaired:
    "Enter the two measurements in matching order — the same number of values in each box.",
  promptSingle: "Enter at least one value and a hypothesised median.",
  errorLength:
    "The lists must pair up one to one, but they are {first} and {second} long.",
  errorZeros:
    "Every difference is zero, so no difference has a direction and the sign test has nothing to count.",
} as const;

type InputMode = "paired" | "single";

function differencesFrom(
  mode: InputMode,
  first: number[],
  second: number[],
  single: number[],
  median: number | null
): number[] | null {
  if (mode === "single") {
    return single.length > 0 && median !== null
      ? single.map((value) => value - median)
      : null;
  }
  if (first.length < 1 || first.length !== second.length) {
    return null;
  }
  return first.map((value, i) => value - second[i]);
}

export default function SignTestCalculatorWidget() {
  const strings = useToolUi(SIGN_TEST_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<InputMode>("paired");
  const [firstText, setFirstText] = useState("");
  const [secondText, setSecondText] = useState("");
  const [singleText, setSingleText] = useState("");
  const [medianText, setMedianText] = useState("0");
  const [alternative, setAlternative] = useState<Alternative>("two-sided");
  const [alpha, setAlpha] = useState("0.05");
  const [level, setLevel] = useState("0.95");

  const first = parseNumberList(firstText);
  const second = parseNumberList(secondText);
  const single = parseNumberList(singleText);
  const median = parseNumberInput(medianText);
  const differences = differencesFrom(mode, first, second, single, median);
  const outcome = differences
    ? signTest(differences, {
        alternative,
        level: Number(level),
        shift: mode === "single" ? (median ?? 0) : 0,
      })
    : null;
  const count = (n: number) =>
    `${fmt(n)}${tPlural(strings.valuesDetected, locale, n)}`;
  const alternatives: Option<Alternative>[] = [
    { value: "two-sided", label: strings.twoSided },
    { value: "less", label: strings.less },
    { value: "greater", label: strings.greater },
  ];

  let notice: string = strings.promptPaired;
  if (mode === "single") {
    notice = strings.promptSingle;
  } else if (
    first.length > 0 &&
    second.length > 0 &&
    first.length !== second.length
  ) {
    notice = t(strings.errorLength, {
      first: fmt(first.length),
      second: fmt(second.length),
    });
  }
  if (outcome && !outcome.ok) {
    notice = strings.errorZeros;
  }

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.inputLabel}
        name="sg-input"
        onChange={setMode}
        options={[
          { value: "paired", label: strings.pairedInput },
          { value: "single", label: strings.singleInput },
        ]}
        value={mode}
      />
      {mode === "paired" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextAreaField
            hint={count(first.length)}
            id="sg-first"
            label={strings.firstLabel}
            onChange={setFirstText}
            placeholder={strings.dataPlaceholder}
            value={firstText}
          />
          <TextAreaField
            hint={count(second.length)}
            id="sg-second"
            label={strings.secondLabel}
            onChange={setSecondText}
            placeholder={strings.dataPlaceholder}
            value={secondText}
          />
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextAreaField
            hint={count(single.length)}
            id="sg-single"
            label={strings.singleLabel}
            onChange={setSingleText}
            placeholder={strings.dataPlaceholder}
            value={singleText}
          />
          <TextField
            hint={strings.medianHint}
            id="sg-median"
            label={strings.medianLabel}
            numeric
            onChange={setMedianText}
            value={medianText}
          />
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-3">
        <SelectField
          hint={strings.alternativeHint}
          id="sg-alternative"
          label={strings.alternativeLabel}
          onChange={setAlternative}
          options={alternatives}
          value={alternative}
        />
        <SelectField
          id="sg-alpha"
          label={strings.alphaLabel}
          onChange={setAlpha}
          options={alphaOptions(fmt)}
          value={alpha}
        />
        <SelectField
          id="sg-level"
          label={strings.levelLabel}
          onChange={setLevel}
          options={levelOptions(fmt)}
          value={level}
        />
      </div>
      <ExampleButton
        label={strings.tryExample}
        onClick={() => {
          setFirstText(strings.exampleFirst);
          setSecondText(strings.exampleSecond);
          setSingleText(strings.exampleSingle);
          setMedianText(strings.exampleMedian);
        }}
      />
      {outcome?.ok ? (
        <SignTestResults
          alpha={Number(alpha)}
          differences={differences ?? []}
          first={mode === "paired" ? first : single}
          fmt={fmt}
          r={outcome.result}
          second={mode === "paired" ? second : null}
          strings={strings}
        />
      ) : (
        <Notice>{notice}</Notice>
      )}
    </div>
  );
}
